<template>
  <div class="full-height">
    <div v-if="items.length" class="table">
      <v-layout class="table__header" align-center>
        <v-flex class="table__checkbox" xs1>
          <v-checkbox
            v-model="checkedAllItems"
            hide-details
            :disabled="selectedAll"
            @change="handleCheckedAllItems"
          />
        </v-flex>
        <v-flex xs8>{{ $t('Common.companyName') | capitalizeFirstLetter }}</v-flex>
        <v-flex xs3>{{ $t('Common.companyDocumentId')}}</v-flex>
      </v-layout>

      <v-list subheader two-line class="table__body">
        <div key="tr__0">
          <v-list-tile class="table__body__tr">
            <v-list-tile-content>
              <v-list-tile-sub-title>
                <v-layout align-center>
                  <v-flex xs4>
                    <strong>{{ firstItemNumber }}</strong>-
                    <strong>{{ lastItemNumber}}</strong>
                    {{ $t('Common.of') }}
                    <strong>{{ pagination.totalElements | formatNumber }}</strong>
                    {{ $t('Common.companies') }}
                  </v-flex>
                  <v-flex xs8 v-if="selectedItems.length">
                    <div class="table__body--text-end">
                      <strong
                        v-if="!selectedAll"
                      >{{ selectedItems.length }} {{ $t(`${namespace}.selectedCompanies`) }}</strong>
                      <strong v-else>{{ $t(`${namespace}.selectedAllCompanies`) }}</strong>
                      <a
                        v-if="pagination.totalElements > 25 && !claims.profiles['ROLE_ADMINISTRATOR'] && !selectedAll"
                        href="#"
                        class="text--normal accent--text"
                        color="accent"
                        flat
                        round
                        @click.prevent="selectAll"
                      >{{ $t(`${namespace}.selectAll`) }}</a>
                      <span
                        v-if="pagination.totalElements > 25 && pagination.totalElements < 100 && !selectedAll"
                      > | </span>
                      <a
                        href="#"
                        class="text--normal accent--text"
                        color="accent"
                        flat
                        round
                        @click.prevent="clearItems"
                      >{{ $t(`${namespace}.clearItems`) }}</a>
                    </div>
                  </v-flex>
                </v-layout>
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider />
        </div>

        <div v-for="(item, i) in items" :key="`tr-${i}`">
          <v-list-tile
            class="table__body__tr"
            :class="{ 'table__body__tr--active': activateLine(item) }"
            @click="handleClick($event, item)"
          >
            <v-list-tile-content>
              <v-list-tile-sub-title>
                <v-layout align-center>
                  <v-flex class="table__checkbox table__td--prevent" xs1>
                    <v-checkbox
                      v-model="selectedItems"
                      :value="item.id"
                      :id="`${item.id}`"
                      :disabled="selectedAll"
                      hide-details
                      @change="handleSelectedItems"
                    />
                  </v-flex>

                  <v-flex xs8>
                    <span class="text--ellipsis" :title="item.name">{{ item.name }}</span>
                  </v-flex>

                  <v-flex xs3>
                    <span :title="item.id" class="text--ellipsis">{{ item.documentId }}</span>
                  </v-flex>
                </v-layout>
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>

          <v-divider />
        </div>
      </v-list>
    </div>

    <v-layout class="py-5" v-else>
      <v-flex>
        <EmptyState image="cost-center" :text="$t(`${namespace}.emptyStateMessage`)" />
      </v-flex>
    </v-layout>

    <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
      <v-container class="px-0">
        <v-pagination
          v-model="pagination.currentPage"
          color="accent"
          :total-visible="totalVisible"
          :length="pagination.totalPages"
          @input="handlePage"
        />
      </v-container>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { formatNumber } from 'utils/functions'
import EmptyState from 'components/EmptyState'

export default {
  name: 'TableSelect',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    selectedAll: {
      type: Boolean,
      default: false
    }
  },

  components: {
    EmptyState
  },
  data () {
    return {
      namespace: 'Views.ConsolidatedReportPage',
      selectedItem: null,
      checkedAllItems: false,
      selectedItems: []
    }
  },
  methods: {
    activateLine (item) {
      return (
        item.id === this.selectedItem ||
        this.selectedItems.find(i => i === item.id)
      )
    },

    handleClick ($event, item) {
      const cls = $event.target.classList
      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item.id
      }
    },

    handleCheckedAllItems (val) {
      if (val) {
        this.selectedItems = this.selectedItems.concat(
          this.items.map(item => item.id)
        )
        this.selectedItems = this.selectedItems.filter(
          (item, i) => this.selectedItems.indexOf(item) === i
        )
        return
      }

      for (const item of this.items) {
        const index = this.selectedItems.indexOf(item.id)
        if (index !== -1) {
          this.selectedItems.splice(index, 1)
        }
      }
    },

    handleSelectedItems () {
      this.checkedAllItems = this.selectedItems.length === this.items.length
    },

    clearItems () {
      this.selectedItems = []
      this.checkedAllItems = false
      this.$emit('selectAllChange', false)
    },

    selectAll () {
      this.$emit('selectAllChange', true)
      this.handleCheckedAllItems(true)
      this.handleSelectedItems()
    },

    handlePage (page) {
      this.$emit('goToPage', page)
    }
  },
  watch: {
    selectedItems (val) {
      this.$emit('selectedItems', val)
    },

    items (val) {
      if (this.selectedAll) {
        this.selectedItems = this.selectedItems.concat(
          val.map(company => company.id)
        )
        this.handleCheckedAllItems(true)
        this.handleSelectedItems()
      }
      if (val) {
        for (const item of val) {
          if (this.selectedItems.indexOf(item.id) < 0) {
            this.checkedAllItems = false
            return
          }
          this.checkedAllItems = true
        }
      }
    }
  },
  computed: {
    ...mapState({
      claims: state => state.userClaims.claims
    }),

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = currentPage * itemsPerPage - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    }
  }
}
</script>

<style lang="scss" scoped>
.table {
  &__header,
  &__body {
    min-width: auto;
  }

  &__header {
    background-color: #f6f5f6;
  }
}
.table__body--text-end {
  text-align: end;
}
</style>
